import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi } from "@/app/lib/api/tasks";
import {
  TaskInsert,
  TaskUpdate,
  TaskAttachmentInsert,
  TaskAttachmentUpdate,
  TaskChecklistItemInsert,
  TaskChecklistItemUpdate,
  TaskCommentInsert,
  TaskCommentUpdate,
  TaskMentionInsert
} from "@/app/lib/supabase/types";
import { taskQueryKeys } from "../queries/useTasks";
import { useAppStore } from "@/app/lib/stores/useAppStore";
import { useAuth } from "@/app/lib/contexts/AuthContext";
import toast from "react-hot-toast";
import { Optional } from "../../types/global";

// Task mutations
/**
 * Hook to create a new task
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  const { selectedCompanyId } = useAppStore();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (taskData: Optional<TaskInsert, "company_id" | "created_by">) => {
      if (!selectedCompanyId) throw new Error("No company selected");
      if (!user?.id) throw new Error("No user logged in");

      return tasksApi.createTask({
        ...taskData,
        company_id: taskData.company_id || selectedCompanyId,
        created_by: taskData.created_by || user.id,
      });
    },
    onSuccess: (newTask) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.byCompany(newTask.company_id)
      });
      if (newTask.project_id) {
        queryClient.invalidateQueries({
          queryKey: taskQueryKeys.byProject(newTask.project_id)
        });
      }
      if (newTask.related_to_id) {
        queryClient.invalidateQueries({
          queryKey: taskQueryKeys.byRelatedToId(newTask.related_to_id)
        });
      }
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.detail(newTask.id)
      });
      toast.success("Task created successfully");
    },
    onError: (error) => {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    },
  });
}

/**
 * Hook to update an existing task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      updates
    }: {
      taskId: string;
      updates: TaskUpdate
    }) => {
      return tasksApi.updateTask(taskId, updates);
    },
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.byCompany(updatedTask.company_id)
      });
      if (updatedTask.project_id) {
        queryClient.invalidateQueries({
          queryKey: taskQueryKeys.byProject(updatedTask.project_id)
        });
      }
      if (updatedTask.related_to_id) {
        queryClient.invalidateQueries({
          queryKey: taskQueryKeys.byRelatedToId(updatedTask.related_to_id)
        });
      }
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.detail(updatedTask.id)
      });
      toast.success("Task updated successfully");
    },
    onError: (error) => {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    },
  });
}

/**
 * Hook to delete a task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();
  const { selectedCompanyId } = useAppStore();

  return useMutation({
    mutationFn: (taskId: string) => {
      return tasksApi.deleteTask(taskId);
    },
    onSuccess: (_, taskId) => {
      queryClient.removeQueries({
        queryKey: taskQueryKeys.detail(taskId)
      });
      if (selectedCompanyId) {
        queryClient.invalidateQueries({
          queryKey: taskQueryKeys.byCompany(selectedCompanyId)
        });
      }
      // Also invalidate all project and stage queries
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.all
      });
      toast.success("Task deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    },
  });
}

// Task Attachment mutations
/**
 * Hook to create a new task attachment
 */
export function useCreateTaskAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentData: TaskAttachmentInsert) => {
      return tasksApi.createTaskAttachment(attachmentData);
    },
    onSuccess: (newAttachment) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.attachments(newAttachment.task_id)
      });
      toast.success("Attachment added successfully");
    },
    onError: (error) => {
      console.error("Error creating task attachment:", error);
      toast.error("Failed to add attachment");
    },
  });
}

/**
 * Hook to update an existing task attachment
 */
export function useUpdateTaskAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attachmentId,
      updates
    }: {
      attachmentId: string;
      updates: TaskAttachmentUpdate
    }) => {
      return tasksApi.updateTaskAttachment(attachmentId, updates);
    },
    onSuccess: (updatedAttachment) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.attachments(updatedAttachment.task_id)
      });
      toast.success("Attachment updated successfully");
    },
    onError: (error) => {
      console.error("Error updating task attachment:", error);
      toast.error("Failed to update attachment");
    },
  });
}

/**
 * Hook to delete a task attachment
 */
export function useDeleteTaskAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: ({ attachmentId, taskId }: { attachmentId: string; taskId: string }) => {
      return tasksApi.deleteTaskAttachment(attachmentId);
    },
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.attachments(taskId)
      });
      toast.success("Attachment deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting task attachment:", error);
      toast.error("Failed to delete attachment");
    },
  });
}

// Task Checklist Item mutations
/**
 * Hook to create a new task checklist item
 */
export function useCreateTaskChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemData: TaskChecklistItemInsert) => {
      return tasksApi.createTaskChecklistItem(itemData);
    },
    onSuccess: (newItem) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.checklistItems(newItem.task_id)
      });
      toast.success("Checklist item added successfully");
    },
    onError: (error) => {
      console.error("Error creating task checklist item:", error);
      toast.error("Failed to add checklist item");
    },
  });
}

/**
 * Hook to update an existing task checklist item
 */
export function useUpdateTaskChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      updates
    }: {
      itemId: string;
      updates: TaskChecklistItemUpdate
    }) => {
      return tasksApi.updateTaskChecklistItem(itemId, updates);
    },
    onSuccess: (updatedItem) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.checklistItems(updatedItem.task_id)
      });
      toast.success("Checklist item updated successfully");
    },
    onError: (error) => {
      console.error("Error updating task checklist item:", error);
      toast.error("Failed to update checklist item");
    },
  });
}

/**
 * Hook to delete a task checklist item
 */
export function useDeleteTaskChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: ({ itemId, taskId }: { itemId: string; taskId: string }) => {
      return tasksApi.deleteTaskChecklistItem(itemId);
    },
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.checklistItems(taskId)
      });
      toast.success("Checklist item deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting task checklist item:", error);
      toast.error("Failed to delete checklist item");
    },
  });
}

/**
 * Hook to reorder task checklist items
 */
export function useReorderTaskChecklistItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      itemOrders
    }: {
      taskId: string;
      itemOrders: { id: string; display_order: number }[]
    }) => {
      return tasksApi.reorderTaskChecklistItems(taskId, itemOrders);
    },
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.checklistItems(taskId)
      });
      toast.success("Checklist items reordered successfully");
    },
    onError: (error) => {
      console.error("Error reordering task checklist items:", error);
      toast.error("Failed to reorder checklist items");
    },
  });
}

// Task Comment mutations
/**
 * Hook to create a new task comment
 */
export function useCreateTaskComment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (commentData: Optional<TaskCommentInsert, "created_by">) => {
      if (!user?.id) throw new Error("No user logged in");

      return tasksApi.createTaskComment({
        ...commentData,
        created_by: commentData.created_by || user.id,
      });
    },
    onSuccess: (newComment) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.comments(newComment.task_id)
      });
      toast.success("Comment added successfully");
    },
    onError: (error) => {
      console.error("Error creating task comment:", error);
      toast.error("Failed to add comment");
    },
  });
}

/**
 * Hook to update an existing task comment
 */
export function useUpdateTaskComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      updates
    }: {
      commentId: string;
      updates: TaskCommentUpdate
    }) => {
      return tasksApi.updateTaskComment(commentId, updates);
    },
    onSuccess: (updatedComment) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.comments(updatedComment.task_id)
      });
      toast.success("Comment updated successfully");
    },
    onError: (error) => {
      console.error("Error updating task comment:", error);
      toast.error("Failed to update comment");
    },
  });
}

/**
 * Hook to delete a task comment
 */
export function useDeleteTaskComment() {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: ({ commentId, taskId }: { commentId: string; taskId: string }) => {
      return tasksApi.deleteTaskComment(commentId);
    },
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.comments(taskId)
      });
      toast.success("Comment deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting task comment:", error);
      toast.error("Failed to delete comment");
    },
  });
}

// Task Mention mutations
/**
 * Hook to create a new task mention
 */
export function useCreateTaskMention() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (mentionData: TaskMentionInsert) => {
      if (!user?.id) throw new Error("No user logged in");

      return tasksApi.createTaskMention(mentionData);
    },
    onSuccess: (newMention) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.mentions(newMention.task_id)
      });
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.byMentionedId(newMention.mentioned_id)
      });
      toast.success("User mentioned successfully");
    },
    onError: (error) => {
      console.error("Error creating task mention:", error);
      toast.error("Failed to mention user");
    },
  });
}

/**
 * Hook to create multiple task mentions at once
 */
export function useCreateTaskMentions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({ taskId, userIds }: { taskId: string; userIds: string[] }) => {
      if (!user?.id) throw new Error("No user logged in");

      const mentions: TaskMentionInsert[] = userIds.map(userId => ({
        // TODO: the label should be the user's name
        label: "user",
        mention_type: "user",
        task_id: taskId,
        mentioned_id: userId,
        created_by: user.id
      }));

      return tasksApi.createTaskMentions(mentions);
    },
    onSuccess: (mentions, { taskId }) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.mentions(taskId)
      });
      // Invalidate queries for all mentioned users
      mentions.forEach(mention => {
        queryClient.invalidateQueries({
          queryKey: taskQueryKeys.byMentionedId(mention.mentioned_id)
        });
      });
      toast.success("Users mentioned successfully");
    },
    onError: (error) => {
      console.error("Error creating task mentions:", error);
      toast.error("Failed to mention users");
    },
  });
}

/**
 * Hook to delete a task mention
 */
export function useDeleteTaskMention() {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: ({ mentionId, taskId, userId }: { mentionId: string; taskId: string; userId: string }) => {
      return tasksApi.deleteTaskMention(mentionId);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: (_, { taskId, userId }) => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.mentions(taskId)
      });
      queryClient.invalidateQueries({
        queryKey: [...taskQueryKeys.all, "mentions"]
      });
      toast.success("Mention removed successfully");
    },
    onError: (error) => {
      console.error("Error deleting task mention:", error);
      toast.error("Failed to remove mention");
    },
  });
}